import { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ApiError } from '../../../shared/api/apiError';
import ModuleNotEnabledState from '../../../shared/components/ModuleNotEnabledState';
import MembershipEditorShell from '../components/MembershipEditorShell';
import MembershipCardPreview from '../components/MembershipCardPreview';
import MembershipTypeBasicsSection from '../components/MembershipTypeBasicsSection';
import MembershipTypePerksEditor from '../components/MembershipTypePerksEditor';
import MembershipTypePointsSection from '../components/MembershipTypePointsSection';
import MembershipTypePricingSection from '../components/MembershipTypePricingSection';
import { MEMBERSHIP_THEME_TOKENS, normalizeMembershipThemeToken, type MembershipThemeToken } from '../themeTokens';
import {
  useCreateMembershipType,
  useMembershipType,
  useUpdateMembershipType
} from '../queries';

type MembershipTypeEditorPageProps = {
  membershipEnabled: boolean;
  typeUuid?: string;
  onNavigate?: (path: string) => void;
  inlineMode?: boolean;
};

type MembershipEditorApiError = {
  status?: number;
  code?: string;
  field?: string;
  message: string;
};

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(80, 'Name is too long'),
  description: z.string().max(240, 'Description is too long').default(''),
  tierLevel: z.number().min(1).max(10),
  themeColorToken: z.enum(MEMBERSHIP_THEME_TOKENS),
  pricePaise: z.number().min(0, 'Price cannot be negative'),
  monthlyPricePaise: z.number().min(0, 'Monthly price cannot be negative'),
  annualPricePaise: z.number().min(0, 'Annual price cannot be negative').optional(),
  validityDays: z.number().min(1).max(3650),
  unlockCriteria: z.string().max(500, 'Unlock criteria is too long').default(''),
  perks: z.array(z.string()),
  pointsEnabled: z.boolean(),
  pointsEarnRatePaisePerPoint: z.number().min(0),
  pointsRedeemPaisePerPoint: z.number().min(0),
  pointsMinRedeem: z.number().min(0),
  pointsExpiryDays: z.number().min(0),
  isActive: z.boolean()
}).superRefine((values, context) => {
  if (values.pointsEnabled) {
    if (values.pointsEarnRatePaisePerPoint <= 0) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['pointsEarnRatePaisePerPoint'], message: 'Earn rate must be > 0' });
    }
    if (values.pointsRedeemPaisePerPoint <= 0) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['pointsRedeemPaisePerPoint'], message: 'Redeem rate must be > 0' });
    }
  }
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  name: '',
  description: '',
  tierLevel: 1,
  themeColorToken: 'standard',
  pricePaise: 0,
  monthlyPricePaise: 0,
  annualPricePaise: 0,
  validityDays: 30,
  unlockCriteria: '',
  perks: [],
  pointsEnabled: false,
  pointsEarnRatePaisePerPoint: 100,
  pointsRedeemPaisePerPoint: 100,
  pointsMinRedeem: 0,
  pointsExpiryDays: 365,
  isActive: true
};

const toForm = (payload: any): FormValues => ({
  name: String(payload?.name || ''),
  description: String(payload?.description || ''),
  tierLevel: Number(payload?.tierLevel || 1),
  themeColorToken: normalizeMembershipThemeToken(payload?.themeColorToken) as MembershipThemeToken,
  pricePaise: Number(payload?.monthlyPricePaise ?? payload?.pricePaise ?? 0),
  monthlyPricePaise: Number(payload?.monthlyPricePaise ?? payload?.pricePaise ?? 0),
  annualPricePaise: Number(payload?.annualPricePaise ?? 0),
  validityDays: Number(payload?.validityDays || 30),
  unlockCriteria: String(payload?.unlockCriteria || ''),
  perks: (() => {
    try {
      const parsed = JSON.parse(String(payload?.perksJson || '[]'));
      return Array.isArray(parsed) ? parsed.map((item) => String(item || '')) : [];
    } catch (_error) {
      return [];
    }
  })(),
  pointsEnabled: Boolean(payload?.pointsEnabled),
  pointsEarnRatePaisePerPoint: Number(payload?.pointsEarnRatePaisePerPoint || 100),
  pointsRedeemPaisePerPoint: Number(payload?.pointsRedeemPaisePerPoint || 100),
  pointsMinRedeem: Number(payload?.pointsMinRedeem || 0),
  pointsExpiryDays: Number(payload?.pointsExpiryDays || 365),
  isActive: Boolean(payload?.isActive ?? true)
});

const normalizeApiError = (error: unknown): MembershipEditorApiError => {
  const apiError = error as ApiError | undefined;
  const data = (apiError?.data && typeof apiError.data === 'object')
    ? apiError.data as {
      error?: string;
      message?: string;
      data?: { code?: string; field?: string };
    }
    : undefined;
  const nestedData = (data?.data && typeof data.data === 'object') ? data.data : undefined;
  return {
    status: apiError?.status,
    code: nestedData?.code,
    field: nestedData?.field,
    message: String(data?.error || data?.message || apiError?.message || 'Unable to save right now. Please retry.')
  };
};

const MembershipTypeEditorPage = ({ membershipEnabled, typeUuid, onNavigate, inlineMode = false }: MembershipTypeEditorPageProps) => {
  const isEditMode = Boolean(typeUuid);
  const existingTypeQuery = useMembershipType(typeUuid, membershipEnabled);
  const createMutation = useCreateMembershipType();
  const updateMutation = useUpdateMembershipType();
  const [submitError, setSubmitError] = useState('');
  const [draftStatus, setDraftStatus] = useState<'idle' | 'restored' | 'saving' | 'saved'>('idle');
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);

  const draftKey = useMemo(() => `mem-draft:${String(typeUuid || 'new')}`, [typeUuid]);

  const {
    watch,
    reset,
    setValue,
    setError,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM
  });

  const watchedRaw = watch();
  const watched = useMemo(
    () => ({ ...EMPTY_FORM, ...(watchedRaw || {}) } as FormValues),
    [watchedRaw]
  );

  useEffect(() => {
    if (!submitError) return;
    setSubmitError('');
  }, [
    submitError,
    watched.description,
    watched.name,
    watched.perks,
    watched.pointsEarnRatePaisePerPoint,
    watched.pointsEnabled,
    watched.pointsRedeemPaisePerPoint,
    watched.pricePaise,
    watched.themeColorToken,
    watched.tierLevel,
    watched.unlockCriteria,
    watched.validityDays
  ]);

  useEffect(() => {
    if (!isEditMode) return;
    if (!existingTypeQuery.data) return;
    reset(toForm(existingTypeQuery.data));
    setDraftStatus('idle');
    setDraftSavedAt(null);
  }, [existingTypeQuery.data, isEditMode, reset]);

  useEffect(() => {
    const raw = localStorage.getItem(draftKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      reset((current) => ({ ...current, ...parsed }));
      setDraftStatus('restored');
      setDraftSavedAt(Date.now());
    } catch (_error) {
      // ignore invalid draft payload
    }
  }, [draftKey, reset]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    setDraftStatus('saving');
    const timer = window.setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify(watched));
      setDraftStatus('saved');
      setDraftSavedAt(Date.now());
    }, 900);
    return () => window.clearTimeout(timer);
  }, [draftKey, isDirty, watched]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const pageTitle = isEditMode ? 'Edit Membership Type' : 'Create Membership Type';
  const pageSubtitle = 'Configure basics, pricing, perks, and points behavior.';
  const readinessItems = useMemo(() => {
    const trimmedName = String(watched.name || '').trim();
    const activePerks = (watched.perks || []).filter((item) => String(item || '').trim().length > 0);
    const pricingConfigured = Number(watched.validityDays || 0) > 0 && Number(watched.pricePaise || 0) >= 0;
    const pointsValid = !watched.pointsEnabled
      || (
        Number(watched.pointsEarnRatePaisePerPoint || 0) > 0
        && Number(watched.pointsRedeemPaisePerPoint || 0) > 0
      );

    return [
      { label: 'Membership name added', complete: trimmedName.length > 0 },
      { label: 'Pricing configured (price set)', complete: pricingConfigured },
      { label: 'At least one perk added', complete: activePerks.length > 0 },
      { label: 'Points config valid when enabled', complete: pointsValid }
    ];
  }, [
    watched.name,
    watched.perks,
    watched.pointsEarnRatePaisePerPoint,
    watched.pointsEnabled,
    watched.pointsRedeemPaisePerPoint,
    watched.pricePaise,
    watched.validityDays
  ]);
  const isPublishReady = readinessItems.every((item) => item.complete);
  const completedCount = readinessItems.filter((item) => item.complete).length;
  const progressPercent = Math.max(0, Math.min(100, (completedCount / readinessItems.length) * 100));
  const progressValue = `${progressPercent}%`;
  const draftStatusLabel = useMemo(() => {
    if (draftStatus === 'saving') {
      return 'Saving draft locally...';
    }
    if (draftStatus === 'saved' && draftSavedAt) {
      return `Draft saved locally at ${new Date(draftSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (draftStatus === 'restored') {
      return 'Restored a locally saved draft.';
    }
    return 'Draft autosaves locally while you edit.';
  }, [draftSavedAt, draftStatus]);
  const publishDisabledReason = isPublishReady ? undefined : 'Complete all requirements to publish';

  // Derive which section is "active" (first incomplete section)
  const sectionStates = useMemo(() => {
    const basicsComplete = String(watched.name || '').trim().length > 0;
    const pricingComplete = Number(watched.validityDays || 0) > 0 && Number(watched.pricePaise || 0) >= 0;
    const perksComplete = (watched.perks || []).filter((p) => String(p || '').trim().length > 0).length > 0;
    const pointsComplete = !watched.pointsEnabled || (Number(watched.pointsEarnRatePaisePerPoint || 0) > 0 && Number(watched.pointsRedeemPaisePerPoint || 0) > 0);

    const states = [basicsComplete, pricingComplete, perksComplete, pointsComplete];
    const firstIncompleteIndex = states.indexOf(false);

    return states.map((complete, i) => {
      if (complete) return 'complete';
      if (i === firstIncompleteIndex) return 'active';
      return 'pending';
    });
  }, [watched.name, watched.validityDays, watched.pricePaise, watched.perks, watched.pointsEnabled, watched.pointsEarnRatePaisePerPoint, watched.pointsRedeemPaisePerPoint]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [isDirty]);

  const handleBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave this page?')) {
      return;
    }
    onNavigate?.('/store/memberships');
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitError('');
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim(),
        tierLevel: values.tierLevel,
        themeColorToken: values.themeColorToken,
        // monthly is the source of truth; price_paise (issuance) mirrors it.
        monthlyPricePaise: values.monthlyPricePaise,
        annualPricePaise: values.annualPricePaise && values.annualPricePaise > 0 ? values.annualPricePaise : undefined,
        pricePaise: values.monthlyPricePaise,
        validityDays: values.validityDays,
        unlockCriteria: values.unlockCriteria.trim(),
        perksJson: JSON.stringify(values.perks.filter((item) => item.trim().length > 0)),
        pointsEnabled: values.pointsEnabled,
        pointsEarnRatePaisePerPoint: values.pointsEnabled ? values.pointsEarnRatePaisePerPoint : 0,
        pointsRedeemPaisePerPoint: values.pointsEnabled ? values.pointsRedeemPaisePerPoint : 0,
        pointsMinRedeem: values.pointsEnabled ? values.pointsMinRedeem : 0,
        pointsExpiryDays: values.pointsEnabled ? values.pointsExpiryDays : 0,
        isActive: values.isActive
      };

      if (isEditMode && typeUuid) {
        await updateMutation.mutateAsync({ typeUuid, input: payload });
      } else {
        await createMutation.mutateAsync(payload as any);
      }
      localStorage.removeItem(draftKey);
      setDraftStatus('idle');
      setDraftSavedAt(null);
      onNavigate?.('/store/memberships');
    } catch (error) {
      const normalized = normalizeApiError(error);
      if (normalized.code === 'MODULE_NOT_ENABLED') {
        setSubmitError('Memberships are not enabled for this store yet.');
        return;
      }
      if (normalized.field === 'name' || normalized.code === 'MEMBERSHIP_TYPE_NAME_EXISTS' || normalized.code === 'MEMBERSHIP_TYPE_NAME_INVALID') {
        setError('name', { type: 'server', message: normalized.message });
        return;
      }
      if (normalized.field === 'tierLevel' || normalized.code === 'MEMBERSHIP_TYPE_TIER_INVALID') {
        setError('tierLevel', { type: 'server', message: normalized.message });
        return;
      }
      if (normalized.field === 'themeColorToken' || normalized.code === 'INVALID_THEME_TOKEN') {
        setError('themeColorToken', { type: 'server', message: normalized.message });
        return;
      }
      if (normalized.field === 'pricePaise' || normalized.field === 'monthlyPricePaise' || normalized.field === 'annualPricePaise' || normalized.field === 'validityDays' || normalized.code === 'INVALID_PRICING') {
        if (normalized.field === 'pricePaise' || normalized.field === 'monthlyPricePaise') {
          setError('monthlyPricePaise', { type: 'server', message: normalized.message });
          return;
        }
        if (normalized.field === 'annualPricePaise') {
          setError('annualPricePaise', { type: 'server', message: normalized.message });
          return;
        }
        if (normalized.field === 'validityDays') {
          setError('validityDays', { type: 'server', message: normalized.message });
          return;
        }
        if (normalized.field === 'pointsEarnRatePaisePerPoint') {
          setError('pointsEarnRatePaisePerPoint', { type: 'server', message: normalized.message });
          return;
        }
        if (normalized.field === 'pointsRedeemPaisePerPoint') {
          setError('pointsRedeemPaisePerPoint', { type: 'server', message: normalized.message });
          return;
        }
        setSubmitError(normalized.message);
        return;
      }
      if (normalized.field === 'unlockCriteria' || normalized.code === 'UNLOCK_CRITERIA_TOO_LONG') {
        setError('unlockCriteria', { type: 'server', message: normalized.message });
        return;
      }
      if (normalized.field === 'perks' || normalized.code === 'PERKS_REQUIRED') {
        setError('perks', { type: 'server', message: normalized.message });
        return;
      }
      setSubmitError(normalized.message || 'Unable to save right now. Please retry.');
    }
  };

  if (!membershipEnabled) {
    return (
      <ModuleNotEnabledState
        title="Memberships are not enabled"
        description="Upgrade your plan to launch membership tiers, points, and customer loyalty journeys."
      />
    );
  }

  // const sectionLabels = ['Basics', 'Pricing', 'Perks', 'Points'];

  const makeBadge = (index: number) => {
    const state = sectionStates[index];
    const badgeClass = state === 'complete' ? 'is-complete' : state === 'active' ? 'is-active' : '';
    return (
      <span className={`membership-editor-shell__section-badge ${badgeClass}`}>
        {state === 'complete' ? <Check size={12} /> : index + 1}
      </span>
    );
  };

  const formSections = (
    <>
      <section className="membership-editor-shell__section">
        <div className="membership-editor-shell__section-header">
          {makeBadge(0)}
          <h4 className="membership-editor-shell__section-title">Basics</h4>
          <span className="membership-editor-shell__section-line" />
        </div>
        <MembershipTypeBasicsSection
          values={{
            name: watched.name ?? '',
            description: watched.description ?? '',
            tierLevel: watched.tierLevel ?? 1,
            themeColorToken: watched.themeColorToken ?? 'standard'
          }}
          onChange={(key, value) => setValue(key as keyof FormValues, value as never, { shouldValidate: true, shouldDirty: true })}
          errors={{
            name: errors.name?.message || '',
            description: errors.description?.message || '',
            tierLevel: errors.tierLevel?.message || '',
            themeColorToken: errors.themeColorToken?.message || ''
          }}
        />
      </section>

      <section className="membership-editor-shell__section">
        <div className="membership-editor-shell__section-header">
          {makeBadge(1)}
          <h4 className="membership-editor-shell__section-title">Pricing</h4>
          <span className="membership-editor-shell__section-line" />
        </div>
        <MembershipTypePricingSection
          monthlyPricePaise={watched.monthlyPricePaise}
          annualPricePaise={watched.annualPricePaise}
          validityDays={watched.validityDays}
          unlockCriteria={watched.unlockCriteria ?? ''}
          onChange={(key, value) => setValue(key as keyof FormValues, value as never, { shouldValidate: true, shouldDirty: true })}
          errors={{
            monthlyPricePaise: errors.monthlyPricePaise?.message || '',
            annualPricePaise: errors.annualPricePaise?.message || '',
            validityDays: errors.validityDays?.message || '',
            unlockCriteria: errors.unlockCriteria?.message || ''
          }}
        />
      </section>

      <section className="membership-editor-shell__section">
        <div className="membership-editor-shell__section-header">
          {makeBadge(2)}
          <h4 className="membership-editor-shell__section-title">Perks</h4>
          <span className="membership-editor-shell__section-line" />
        </div>
        <MembershipTypePerksEditor
          perks={watched.perks}
          onChange={(perks) => setValue('perks', perks, { shouldValidate: true, shouldDirty: true })}
          error={errors.perks?.message || ''}
        />
      </section>

      <section className="membership-editor-shell__section">
        <div className="membership-editor-shell__section-header">
          {makeBadge(3)}
          <h4 className="membership-editor-shell__section-title">Points</h4>
          <span className="membership-editor-shell__section-line" />
        </div>
        <MembershipTypePointsSection
          values={{
            pointsEnabled: watched.pointsEnabled ?? false,
            pointsEarnRatePaisePerPoint: watched.pointsEarnRatePaisePerPoint ?? 0,
            pointsRedeemPaisePerPoint: watched.pointsRedeemPaisePerPoint ?? 0,
            pointsMinRedeem: watched.pointsMinRedeem ?? 0,
            pointsExpiryDays: watched.pointsExpiryDays ?? 0
          }}
          onChange={(key, value) => setValue(key as keyof FormValues, value as never, { shouldValidate: true, shouldDirty: true })}
          errors={{
            points: '',
            pointsEarnRatePaisePerPoint: errors.pointsEarnRatePaisePerPoint?.message || '',
            pointsRedeemPaisePerPoint: errors.pointsRedeemPaisePerPoint?.message || '',
            pointsMinRedeem: errors.pointsMinRedeem?.message || '',
            pointsExpiryDays: errors.pointsExpiryDays?.message || ''
          }}
        />
      </section>
    </>
  );

  const livePreview = (
      <MembershipCardPreview
        name={watched.name}
        description={watched.description}
        tierLevel={watched.tierLevel}
        pricePaise={watched.monthlyPricePaise}
        validityDays={watched.validityDays}
        themeColorToken={watched.themeColorToken}
        perks={watched.perks}
        readinessItems={readinessItems}
      />
  );

  const content = inlineMode ? (
    <>
      <div className="membership-editor-inline-bar">
        <button
          className="btn primary"
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || !isPublishReady}
          title={publishDisabledReason}
        >
          {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Publish'}
        </button>
      </div>
      {submitError ? <p className="appointments-v2-empty-copy">{submitError}</p> : null}
      <div className="membership-editor-inline-grid">
        <div style={{ display: 'grid', gap: 16 }}>
          {formSections}
        </div>
        {livePreview}
      </div>
    </>
  ) : (
    <MembershipEditorShell
      title={pageTitle}
      subtitle={pageSubtitle}
      meta={draftStatusLabel}
      breadcrumb={isEditMode ? 'Edit' : 'Create New'}
      progress={(
        <div className="membership-editor-shell__progress-track" aria-hidden="true">
          <div className="membership-editor-shell__progress-fill" style={{ width: progressValue }} />
        </div>
      )}
      progressLabel={`${completedCount}/${readinessItems.length} complete`}
      leftColumn={(
        <>
          {submitError ? <p className="appointments-v2-empty-copy">{submitError}</p> : null}
          {formSections}
          <div className="membership-editor-shell__bottom-actions">
            <button className="btn ghost" type="button" onClick={handleBack}>Back</button>
            <button
              className="btn primary"
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting || !isPublishReady}
              title={publishDisabledReason}
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Publish'}
            </button>
          </div>
        </>
      )}
      rightColumn={livePreview}
    />
  );

  return inlineMode ? content : (
    <section className="appointments-v2-page">
      {content}
    </section>
  );
};

export default MembershipTypeEditorPage;
